import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { ClanRequest } from '../../types/clan';
import clanService from '../../services/clanService';
import FieldInfoButton from '../field/FieldInfoButton';
import { clanApplicationsManagementInfo } from './clanRequestsInfo';
import { useNotification } from '../../hooks/useNotification';
import NotificationContainer from '../common/NotificationContainer';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isModernTheme = (theme: any) => theme.name === 'modernWarfare';
const isShadowTheme = (theme: any) => theme.name === 'shadowGrid';

interface ClanApplicationsTabProps {
  clanId: string;
  canManage: boolean;
  isActive?: boolean;
}

const ClanApplicationsTab: React.FC<ClanApplicationsTabProps> = ({ clanId, canManage, isActive = true }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { notifications, removeNotification, showError } = useNotification();
  const [applications, setApplications] = useState<ClanRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; type: 'approve' | 'decline'; application: ClanRequest | null }>({
    show: false,
    type: 'approve',
    application: null
  });

  useEffect(() => {
    if (isActive && clanId) {
      loadApplications();
    }
  }, [clanId, isActive]);

  const loadApplications = async () => {
    if (!canManage) {
      setError('You don\'t have permission to view applications');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await clanService.getClanApplications(clanId);
      setApplications(data);
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (application: ClanRequest) => {
    if (processingIds.has(application.userId)) return;
    setConfirmModal({ show: true, type: 'approve', application });
  };

  const handleConfirmAction = async () => {
    if (!confirmModal.application) return;
    
    const { type, application } = confirmModal;
    setConfirmModal({ show: false, type: 'approve', application: null });
    
    try {
      setProcessingIds(prev => new Set(prev).add(application.userId));
      
      if (type === 'approve') {
        await clanService.approveApplication(clanId, application.userId);
      } else {
        await clanService.declineApplication(clanId, application.userId);
      }
      
      await loadApplications();
    } catch (err: any) {
      console.error(`Error ${type}ing application:`, err);
      showError('Error', err.message || `Failed to ${type} application`);
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(application.userId);
        return newSet;
      });
    }
  };

  const handleDecline = async (application: ClanRequest) => {
    if (processingIds.has(application.userId)) return;
    setConfirmModal({ show: true, type: 'decline', application });
  };

  const handleUserClick = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading applications...</LoadingSpinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <RetryButton onClick={loadApplications}>Retry</RetryButton>
      </Container>
    );
  }

  if (!canManage) {
    return (
      <Container>
        <ErrorMessage>You don't have permission to view applications</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TitleRow>
          <Title>Applications ({applications.length})</Title>
          <FieldInfoButton title="Managing Applications" sections={clanApplicationsManagementInfo} />
        </TitleRow>
        <RefreshButton onClick={loadApplications} disabled={loading}>
          🔄 Refresh
        </RefreshButton>
      </Header>

      {applications.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📝</EmptyIcon>
          <EmptyTitle>No Applications</EmptyTitle>
          <EmptyDescription>
            No one has applied to join your clan yet.
          </EmptyDescription>
        </EmptyState>
      ) : (
        <ApplicationsList>
          {applications.map((application) => (
            <ApplicationCard key={application.id}>
              <ApplicationInfo onClick={() => handleUserClick(application.userId)}>
                <UserName>{application.userName}</UserName>
                <ApplicationDate>
                  Applied {clanService.formatRequestDate(application.createdAt)}
                </ApplicationDate>
                <ClanType>{application.clanType}</ClanType>
              </ApplicationInfo>
              
              <ApplicationActions>
                <ApproveButton
                  onClick={() => handleApprove(application)}
                  disabled={processingIds.has(application.userId)}
                >
                  {processingIds.has(application.userId) ? '...' : '✅ Approve'}
                </ApproveButton>
                <DeclineButton
                  onClick={() => handleDecline(application)}
                  disabled={processingIds.has(application.userId)}
                >
                  {processingIds.has(application.userId) ? '...' : '❌ Decline'}
                </DeclineButton>
              </ApplicationActions>
            </ApplicationCard>
          ))}
        </ApplicationsList>
      )}
      
      {/* Модальное окно подтверждения */}
      {confirmModal.show && confirmModal.application && (
        <ConfirmModalOverlay>
          <ConfirmModal>
            <ConfirmModalHeader>
              <ConfirmModalTitle>
                {confirmModal.type === 'approve' ? 'Approve Application' : 'Decline Application'}
              </ConfirmModalTitle>
            </ConfirmModalHeader>
            
            <ConfirmModalBody>
              <UserAvatar>{confirmModal.application.userName.charAt(0).toUpperCase()}</UserAvatar>
              <ConfirmText>
                Are you sure you want to <strong>{confirmModal.type}</strong> <br/>
                <UserNameHighlight>{confirmModal.application.userName}</UserNameHighlight>'s application?
              </ConfirmText>
              
              {confirmModal.type === 'approve' && (
                <ApprovalNote>
                  ✅ This user will be added to the clan immediately
                </ApprovalNote>
              )}
              
              {confirmModal.type === 'decline' && (
                <DeclineNote>
                  ❌ This application will be permanently declined
                </DeclineNote>
              )}
            </ConfirmModalBody>
            
            <ConfirmModalActions>
              <CancelButton onClick={() => setConfirmModal({ show: false, type: 'approve', application: null })}>
                Cancel
              </CancelButton>
              <ConfirmButton 
                type={confirmModal.type}
                onClick={handleConfirmAction}
              >
                {confirmModal.type === 'approve' ? '✅ Approve' : '❌ Decline'}
              </ConfirmButton>
            </ConfirmModalActions>
          </ConfirmModal>
        </ConfirmModalOverlay>
      )}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 20px;
  background: transparent;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
`;

const RefreshButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => isShadowTheme(theme) ? '#000000' : '#00121f'};
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.3s ease;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.7)};
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => withAlpha(theme.colors.error, 0.1)};
  color: ${({ theme }) => theme.colors.error};
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.error, 0.3)};
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
`;

const RetryButton = styled.button`
  background: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.error, 0.8)};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.6)};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.8)};
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 1rem;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.6)};
  opacity: 0.8;
`;

const ApplicationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ApplicationCard = styled.div`
  background: ${({ theme }) => withAlpha(theme.colors.surface, 0.8)};
  backdrop-filter: blur(10px);
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ApplicationInfo = styled.div`
  flex: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 6px;
  
  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.1)};
    transform: translateY(-1px);
  }
`;

const UserName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.9)};
  margin-bottom: 4px;
  transition: color 0.2s ease;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
  
  ${ApplicationInfo}:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ApplicationDate = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.6)};
  margin-bottom: 4px;
`;

const ClanType = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.secondary};
  text-transform: capitalize;
  font-weight: 500;
`;

const ApplicationActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ApproveButton = styled.button`
  background: ${({ theme }) => theme.colors.success};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 90px;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => withAlpha(theme.colors.success, 0.8)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeclineButton = styled.button`
  background: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  min-width: 90px;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => withAlpha(theme.colors.error, 0.8)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Модальное окно подтверждения
const ConfirmModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
`;

const ConfirmModal = styled.div`
  background: ${({ theme }) => isModernTheme(theme) 
    ? withAlpha(theme.colors.surface, 0.95) 
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))'};
  backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  border-radius: 16px;
  width: 90%;
  max-width: 450px;
  padding: 0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ConfirmModalHeader = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
`;

const ConfirmModalTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
`;

const ConfirmModalBody = styled.div`
  padding: 24px;
  text-align: center;
`;

const UserAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ theme }) => theme.gradients.primary};
  color: ${({ theme }) => isShadowTheme(theme) ? '#000000' : '#00121f'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  margin: 0 auto 16px auto;
  box-shadow: 0 4px 12px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
`;

const ConfirmText = styled.p`
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.9)};
  font-size: 1.1rem;
  line-height: 1.5;
  margin-bottom: 16px;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
`;

const UserNameHighlight = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
`;

const ApprovalNote = styled.div`
  background: ${({ theme }) => withAlpha(theme.colors.success, 0.15)};
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.success, 0.3)};
  color: ${({ theme }) => theme.colors.success};
  padding: 12px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-top: 16px;
`;

const DeclineNote = styled.div`
  background: ${({ theme }) => withAlpha(theme.colors.error, 0.15)};
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.error, 0.3)};
  color: ${({ theme }) => theme.colors.error};
  padding: 12px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-top: 16px;
`;

const ConfirmModalActions = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  justify-content: center;
`;

const CancelButton = styled.button`
  background: transparent;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.7)};
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.text, 0.3)};
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.text, 0.1)};
    color: ${({ theme }) => withAlpha(theme.colors.text, 0.9)};
    border-color: ${({ theme }) => withAlpha(theme.colors.text, 0.5)};
  }
`;

const ConfirmButton = styled.button<{ type: 'approve' | 'decline' }>`
  background: ${props => props.type === 'approve' ? props.theme.colors.success : props.theme.colors.error};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: 0 4px 12px ${props => props.type === 'approve' ? withAlpha(props.theme.colors.success, 0.3) : withAlpha(props.theme.colors.error, 0.3)};
  
  &:hover {
    background: ${props => props.type === 'approve' ? withAlpha(props.theme.colors.success, 0.8) : withAlpha(props.theme.colors.error, 0.8)};
    transform: translateY(-1px);
    box-shadow: 0 6px 16px ${props => props.type === 'approve' ? withAlpha(props.theme.colors.success, 0.4) : withAlpha(props.theme.colors.error, 0.4)};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export default ClanApplicationsTab;